import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Send } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api, { resolveImg } from "@/lib/api";
import { useSettings } from "@/lib/settings";

const NAV_LINKS = [
    { path: "/", label: "Главная" },
    { path: "/catalog", label: "Каталог" },
    { path: "/configurator", label: "Конфигуратор" },
    { path: "/services", label: "Услуги" },
    { path: "/reviews", label: "Отзывы" },
    { path: "/blog", label: "Блог" },
    { path: "/about", label: "О нас" },
    { path: "/contacts", label: "Контакты" },
];

export default function Navbar() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const settings = useSettings();
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        setIsOpen(false);
    }, [location]);

    const logoUrl = settings?.logo_url ? resolveImg(settings.logo_url) : null;

    return (
        <nav
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled ? "bg-black/90 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
            }`}
            data-testid="navbar"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
                <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
                    {logoUrl && <img src={logoUrl} alt="AutoTreider" className="h-12 w-auto" />}
                </Link>

                <div className="hidden lg:flex items-center gap-8">
                    {NAV_LINKS.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className={`text-xs uppercase tracking-[0.2em] transition-colors ${
                                location.pathname === link.path ? "text-white" : "text-zinc-500 hover:text-white"
                            }`}
                            data-testid={`nav-link-${link.path.slice(1) || "home"}`}
                        >
                            {link.label}
                        </Link>
                    ))}
                </div>

                <div className="flex items-center gap-4">
                    {settings?.telegram_channel_url && (
                        <a
                            href={settings.telegram_channel_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hidden md:flex items-center gap-2 bg-[#0099FF] text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold transition-all hover:shadow-[0_0_15px_rgba(0,153,255,0.5)]"
                            data-testid="nav-telegram-btn"
                        >
                            <Send className="w-4 h-4" />
                            {settings.telegram_channel_button_text || "Telegram"}
                        </a>
                    )}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="lg:hidden text-white p-2"
                        data-testid="nav-mobile-toggle"
                    >
                        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="lg:hidden bg-black/95 backdrop-blur-xl border-b border-white/5"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {NAV_LINKS.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={`text-sm uppercase tracking-wider py-2 ${
                                        location.pathname === link.path ? "text-[#0099FF]" : "text-zinc-400"
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                            {settings?.telegram_channel_url && (
                                <a
                                    href={settings.telegram_channel_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 bg-[#0099FF] text-white px-5 py-3 text-sm uppercase tracking-wider font-bold mt-2"
                                >
                                    <Send className="w-4 h-4" />
                                    {settings.telegram_channel_button_text || "Telegram"}
                                </a>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
}
